import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { usePrinter } from 'Src/feature/printer/usePrinter';
import { useOnErrorShowToast } from 'Src/feature/toasts/useOnErrorShowToast';

import styles from './ReceiptPrintTool.module.scss';

interface FormModel {
  printerId: number | string;
  checkId: number | string;
  paymentMethodId: number | string;
  isEMV: boolean;
  templateId: string;
  simulateBackup:  boolean;
  targetRealPrinter:  boolean;
  isPayment:  boolean;
}

const { Form, Field } = getTypedFormComponents<FormModel>();
const sectionId = 'ReceiptPrintTool';

const ReceiptPrintTool: FC = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t }= useTranslation();
  const printerGetAll = usePrinter.query.getAll();
  const printerUatPayment = usePrinter.mutate.printUATPayment(
    useOnErrorShowToast({ title: t('printer.errors.uatPrintFail') })
  );
  const printerUatGuest = usePrinter.mutate.printUATGuest(
    useOnErrorShowToast({ title: t('printer.errors.uatGuestFail') })
  );
  const ccPaymentMethods = usePaymentMethod.query.ccMethods();
  const checkHeader = useCheckHeader.query.collection();

  const printerOptions = (printerGetAll.data || []).map((p) => ({ value: p.id, label: p.name }));
  const checkIdOptions = (checkHeader.data?.all || []).map((c) => ({ value: c.id, label: c.id }));
  const paymentMethodOptions = ccPaymentMethods.data?.map((pm) => ({ value: pm.id, label: pm.name })) ?? [];

  const form = useForm<FormModel>({
    defaultValues: {
      printerId: printerOptions[0]?.value ?? '',
      checkId: checkIdOptions[0]?.value ?? '',
      paymentMethodId: paymentMethodOptions[0]?.value ?? '',
      isEMV: false,
      templateId: '0',
      simulateBackup: false,
      targetRealPrinter: false,
      isPayment: false,
    },
  });

  const formValues = form.state.watch();

  const onUatPayment = async () => {
    await form.action.submitIfValid(async ({ values }) => {
      const response = await printerUatPayment.mutateAsync({
        printerId: typeof values.printerId === 'number' ? values.printerId : 0,
        paymentMethodId: typeof values.paymentMethodId === 'number' ? values.paymentMethodId : 0,
        checkId: typeof values.checkId === 'number' ? values.checkId : undefined,
        isEMV: values.isEMV,
        templateId: values.templateId === '0' ? undefined : parseInt(values.templateId, 10),
        simulateBackupPrinter: values.simulateBackup,
      });
      log.info('onUatPayment()', { response });
    });
  };

  const onUatGuest = async () => {
    await form.action.submitIfValid(async ({ values }) => {
      const response = await printerUatGuest.mutateAsync({
        printerId: typeof values.printerId === 'number' ? values.printerId : 0,
        checkId: typeof values.checkId === 'number' ? values.checkId : undefined,
        templateId: values.templateId === '0' ? undefined : parseInt(values.templateId, 10),
        simulateBackupPrinter: values.simulateBackup,
        targetRealPrinter: values.targetRealPrinter,
      });
      log.info('onUatGuest', { response });
    });
  };

  return (
    <div className={styles.root}>
      <Copy variant="h5" value="Create Receipt Print Request" />
      <Form usedForm={form} fullWidth size="sm" omitChangeTracking>
        <Field
          type="switch"
          name="isPayment"
          label="isPayment - Toggle ON for UAT.Payment / Toggle OFF for UAT.Guest"
        />
        <Field
          type="number"
          name="templateId"
          label="Template ID"
          classes={{ input: styles.input }}
        />
        <Field
          type="select"
          name="printerId"
          options={printerOptions}
          label="Printer"
        />
        <Field
          type="select"
          name="checkId"
          options={checkIdOptions}
          label="Check ID"
        />
        <Field
          type="select"
          name="paymentMethodId"
          options={paymentMethodOptions}
          label="Payment Method"
          disabled={!formValues.isPayment}
        />
        <Field
          type="switch"
          name="isEMV"
          label="isEMV"
          disabled={!formValues.isPayment}
        />
        <Field
          type="switch"
          name="simulateBackup"
          label="simulateBackup"
        />
        <Field
          type="switch"
          name="targetRealPrinter"
          label="targetRealPrinter"
          disabled={formValues.isPayment}
        />
      </Form>
      <CTA
        label="Create Request"
        onClick={() => {
          if (formValues.isPayment) void onUatPayment();
          else void onUatGuest();
        }}
        className={styles.cta}
      />
    </div>
  );
};

export { ReceiptPrintTool };
